/**
 * OKS-404 / OKS-561 — Locale-aware date formatting for the paused member
 * view.
 *
 * The server ships ISO strings (KD-7); all human formatting happens here
 * so the backend stays locale-agnostic. Day-first ordering ("28 Apr 2026")
 * is intentional for this surface — it matches the W26 spec example
 * ("12 Jan – 28 Apr 2026") and reads cleanly in both locales. We map the
 * app's `"en" | "fr"` i18next language to a day-first BCP-47 tag
 * (`en-GB` / `fr-FR`) for `toLocaleDateString`.
 *
 * OKS-561 — the optional `timeZone` (an IANA name shipped as the
 * `brand_timezone` prop) pins the calendar day to the BRAND's zone rather
 * than the viewer's, so "Active {start}–{end}" reads identically for a
 * creator in any locale/zone (TRD KD-7). Omitting it falls back to the
 * runtime's local zone (preserves OKS-404 test call-sites).
 */

type Lang = string

const EN_DASH = "–" // – (period separator)
const EM_DASH = "—" // — (unknown-end placeholder)

function bcp47(lang: Lang): string {
  return lang === "en" ? "en-GB" : "fr-FR"
}

/** Builds `toLocaleDateString` options, injecting `timeZone` when given. */
function dateOptions(
  base: Intl.DateTimeFormatOptions,
  timeZone?: string,
): Intl.DateTimeFormatOptions {
  return timeZone ? { ...base, timeZone } : base
}

/**
 * Format a single ISO date as a day-first short date, e.g. "28 Apr 2026"
 * / "28 avr. 2026". Returns `null` for nullish/invalid input so callers
 * can branch to the no-date copy. `timeZone` (IANA) pins the day to the
 * brand's zone.
 */
export function formatPauseDate(
  iso: string | null,
  lang: Lang,
  timeZone?: string,
): string | null {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null

  return date.toLocaleDateString(
    bcp47(lang),
    dateOptions({ day: "numeric", month: "short", year: "numeric" }, timeZone),
  )
}

/**
 * Format the active period "{join} – {pause}".
 *
 * - Both dates present, same calendar year → year is shown once on the
 *   end only ("12 Jan – 28 Apr 2026").
 * - Both present, different years → year on both ("12 Jan 2025 – 3 Feb 2026").
 * - Pause date unknown (the no-pause-event edge) → "{join} – —" (em dash).
 * - Join date unparseable → returns an em dash alone (defensive; never
 *   throws).
 */
export function formatActivePeriod(
  joinIso: string | null,
  pauseIso: string | null,
  lang: Lang,
  timeZone?: string,
): string {
  const locale = bcp47(lang)
  const join = joinIso ? new Date(joinIso) : null
  if (!join || Number.isNaN(join.getTime())) return EM_DASH

  const pause = pauseIso ? new Date(pauseIso) : null
  const pauseValid = pause && !Number.isNaN(pause.getTime())

  if (!pauseValid) {
    const start = join.toLocaleDateString(
      locale,
      dateOptions(
        { day: "numeric", month: "short", year: "numeric" },
        timeZone,
      ),
    )
    return `${start} ${EN_DASH} ${EM_DASH}`
  }

  const sameYear = join.getFullYear() === pause.getFullYear()
  const start = join.toLocaleDateString(
    locale,
    dateOptions(
      {
        day: "numeric",
        month: "short",
        ...(sameYear ? {} : { year: "numeric" }),
      },
      timeZone,
    ),
  )
  const end = pause.toLocaleDateString(
    locale,
    dateOptions({ day: "numeric", month: "short", year: "numeric" }, timeZone),
  )

  return `${start} ${EN_DASH} ${end}`
}
